import React from 'react';
import { Box } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import DatePicker from '@/components/@IntranetLibrary/DatePicker';
import Select from '@/components/@IntranetLibrary/Select';
import dayjs, { Dayjs } from 'dayjs';
import { formatAs } from '@/utils/format';

type Props = {
  readonly defaultValue?: Date | null;
  readonly name: string;
  readonly triggerField?: string;
};

const generateTimeOptions = () => {
  const options: { label: string; value: string }[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const timeString = `${String(hour).padStart(2, '0')}:${String(
        minute,
      ).padStart(2, '0')}`;
      options.push({ label: timeString, value: timeString });
    }
  }
  return options;
};

const RHFDateTimePicker: React.FC<Props> = ({
  defaultValue = null,
  name,
  triggerField,
}) => {
  const {
    control,
    trigger,
    formState: { errors },
  } = useFormContext();

  const error = errors[name];

  return (
    <Box display="flex" flexDirection="row" gap="12px">
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          // RHF 저장 값: Date | null
          const valueDate: Date | null = field.value ?? null;

          // DatePicker에 줄 값: Dayjs | null
          const pickerValue: Dayjs | null = valueDate ? dayjs(valueDate) : null;

          const handleDateChange = (newVal: Dayjs | null) => {
            // Dayjs -> Date 로 변환해 RHF에 저장
            const asDate = newVal?.isValid() ? newVal.toDate() : null;
            field.onChange(asDate);
            if (triggerField) trigger(triggerField);
          };

          const handleTimeChange = (timeStr: string) => {
            const [hours, minutes] = timeStr.split(':').map(Number);
            const base = valueDate ? dayjs(valueDate) : dayjs();
            const next = base
              .hour(hours)
              .minute(minutes)
              .second(0)
              .millisecond(0);
            field.onChange(next.toDate()); // Dayjs -> Date
            if (triggerField) trigger(triggerField);
          };

          const timeValue = valueDate ? formatAs(valueDate, 'time') : '';

          return (
            <>
              <DatePicker
                value={pickerValue}
                onChange={handleDateChange}
                defaultValue={defaultValue ? dayjs(defaultValue) : null}
                placeholder="날짜 선택"
                format="YYYY-MM-DD"
                sx={{ width: '200px' }}
                error={error}
              />
              <Select
                disabled={!valueDate}
                placeholder="시간 선택"
                options={generateTimeOptions()}
                value={valueDate ? timeValue : ''}
                onChange={(event) =>
                  handleTimeChange(String(event.target.value))
                }
                error={Boolean(error)}
                sx={{ width: '140px' }}
              />
            </>
          );
        }}
      />
    </Box>
  );
};

export default RHFDateTimePicker;
